import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PROJEKT_WAEHRUNG, betrag, type BelegAnsicht, type BelegArt, type BudgetStand } from '@beholder/shared/module/budget';
import type { Mitglied } from '@beholder/shared';
import { api } from '../../core/api';
import { Symbol } from '../../core/symbole';
import { Auswahl, Eingabe, Knopf, Leer, Meldung, datum, useAktion } from '../../core/ui';
import { ART_NAME, StatusMarke, type DialogArt } from './BelegDialog';
import { Euro } from './Euro';
import { LabelMarken } from './Labels';
import { pfad } from './hilfe';

type Filter = BelegArt | 'alle';
const W = PROJEKT_WAEHRUNG;

/** Schlüssel für den Personen-Filter: Mitglieds-ID oder „frei:<name>“. */
const werSchluessel = (b: BelegAnsicht) => b.teilnehmerId ?? (b.teilnehmerFrei ? `frei:${b.teilnehmerFrei.trim().toLocaleLowerCase('de')}` : '');

/** Liste der Rechnungen, Angebote und Einnahmen mit Filtern. Ein Klick öffnet den Beleg. Beträge in Euro. */
export function Belege({
  projektId,
  s,
  mitglieder,
  darfSchreiben,
  oeffnen,
  neuLaden,
}: {
  projektId: string;
  s: BudgetStand;
  mitglieder: Mitglied[] | null;
  darfSchreiben: boolean;
  oeffnen(m: DialogArt): void;
  neuLaden(): void;
}) {
  const [such, setSuch] = useSearchParams();
  const art = (such.get('art') as Filter | null) ?? 'rechnung';
  const kategorie = such.get('kategorie') ?? '';
  const label = such.get('label') ?? '';
  const [suche, setSuche] = useState('');
  const [status, setStatus] = useState('');
  const [wer, setWer] = useState('');
  const a = useAktion();

  const setzen = (k: string, v: string) => {
    const n = new URLSearchParams(such);
    if (v) n.set(k, v);
    else n.delete(k);
    setSuch(n, { replace: true });
  };

  // Kategorie-Filter schließt Unterkategorien ein
  const kategorien = useMemo(() => {
    if (!kategorie) return null;
    const ids = new Set([kategorie]);
    let neu = true;
    while (neu) {
      neu = false;
      for (const k of s.kategorien) if (k.elternId && ids.has(k.elternId) && !ids.has(k.id)) (ids.add(k.id), (neu = true));
    }
    return ids;
  }, [kategorie, s.kategorien]);

  const t = suche.trim().toLocaleLowerCase('de');
  const liste = s.belege.filter(
    (b) =>
      (art === 'alle' || b.art === art) &&
      (art === 'einnahme' || !kategorien || (kategorie === '__ohne' ? !b.kategorieId : !!b.kategorieId && kategorien.has(b.kategorieId))) &&
      (!status || b.status === status) &&
      (art !== 'einnahme' || !label || (label === '__ohne' ? b.art === 'einnahme' && !b.labels.length : b.labels.some((l) => l.toLocaleLowerCase('de') === label))) &&
      (!wer || (wer === '__ohne' ? !werSchluessel(b) : werSchluessel(b) === wer)) &&
      (!t || `${b.titel} ${b.absender} ${b.notiz} ${b.kategoriePfad ?? ''} ${b.teilnehmerName ?? ''} ${b.labels.join(' ')}`.toLocaleLowerCase('de').includes(t)),
  );
  // Summen nur über zählende Belege (ganze Cent, Euro)
  let summeRechnungen = 0;
  let summeAngebote = 0;
  let summeEinnahmen = 0;
  let ohneKurs = 0;
  for (const b of liste) {
    if (b.art === 'angebot' && b.status !== 'offen') continue;
    if (b.umgerechnetCent === null) ohneKurs++;
    else if (b.art === 'rechnung') summeRechnungen += b.umgerechnetCent;
    else if (b.art === 'angebot') summeAngebote += b.umgerechnetCent;
    else summeEinnahmen += b.umgerechnetCent;
  }
  const summen = [
    (art === 'rechnung' || art === 'alle') && `Ausgaben ${betrag(summeRechnungen, W)}`,
    (art === 'angebot' || art === 'alle') && `offene Angebote ${betrag(summeAngebote, W)}`,
    (art === 'einnahme' || art === 'alle') && `Einnahmen ${betrag(summeEinnahmen, W)}`,
  ].filter(Boolean);
  const nachholbar = s.belege.filter((b) => b.kursQuelle === 'ersatz' || b.kursQuelle === 'fehlt').length;
  const anzahl = (x: BelegArt) => s.belege.filter((b) => b.art === x).length;
  const reiter: Record<Filter, string> = {
    rechnung: `Rechnungen (${anzahl('rechnung')})`,
    angebot: `Angebote (${anzahl('angebot')})`,
    einnahme: `Einnahmen (${anzahl('einnahme')})`,
    alle: 'Alle',
  };

  async function nachholen() {
    const r = await a.ausfuehren(() => api.post<{ aktualisiert: number }>(pfad(projektId, '/kurse/nachholen')));
    if (r) {
      a.setFehler(r.aktualisiert ? '' : 'Die Kursquelle ist noch nicht erreichbar oder hat keine neuen Kurse – bitte später noch einmal versuchen.');
      neuLaden();
    }
  }

  return (
    <div className="stapel">
      <div className="bu-werkzeug">
        <div className="bu-schalter" role="tablist" aria-label="Belegart">
          {(['rechnung', 'angebot', 'einnahme', 'alle'] as const).map((x) => (
            <button key={x} type="button" role="tab" aria-selected={art === x} className={art === x ? 'aktiv' : ''} onClick={() => setzen('art', x === 'rechnung' ? '' : x)}>
              {reiter[x]}
            </button>
          ))}
        </div>
        <span className="abstand" />
        {darfSchreiben && (
          <>
            <Knopf art="haupt" onClick={() => oeffnen({ art: 'neu', belegArt: 'rechnung' })}>
              {Symbol.plus} Rechnung
            </Knopf>
            <Knopf onClick={() => oeffnen({ art: 'neu', belegArt: 'angebot' })}>{Symbol.plus} Angebot</Knopf>
            <Knopf onClick={() => oeffnen({ art: 'neu', belegArt: 'einnahme' })}>{Symbol.plus} Einnahme</Knopf>
          </>
        )}
      </div>
      {art === 'angebot' && (
        <p className="leise klein bu-ohne-abstand">Angebote sind mögliche Kosten: Sie zählen nicht als Ausgabe, werden aber im Budget als „offene Angebote“ gezeigt.</p>
      )}
      {art === 'einnahme' && (
        <p className="leise klein bu-ohne-abstand">Einnahmen (Zuschüsse, Sponsoring, Verkäufe …) zählen nicht gegen das Budget, sondern in den Saldo: Einnahmen − Ausgaben.</p>
      )}

      <div className="bu-filter">
        <Eingabe type="search" placeholder="Suchen (Titel, Absender, Notiz, Person) …" value={suche} onChange={(e) => setSuche(e.target.value)} aria-label="Suchen" />
        {art === 'einnahme' ? (
          <Auswahl value={label} onChange={(e) => setzen('label', e.target.value)} aria-label="Label">
            <option value="">Alle Labels</option>
            <option value="__ohne">Ohne Label</option>
            {s.labels.map((l) => (
              <option key={l} value={l.toLocaleLowerCase('de')}>
                {l}
              </option>
            ))}
          </Auswahl>
        ) : (
          <Auswahl value={kategorie} onChange={(e) => setzen('kategorie', e.target.value)} aria-label="Kategorie">
            <option value="">Alle Kategorien</option>
            <option value="__ohne">Ohne Kategorie</option>
            {s.kategorien.map((k) => (
              <option key={k.id} value={k.id}>
                {'  '.repeat(k.tiefe)}
                {k.name}
              </option>
            ))}
          </Auswahl>
        )}
        <Auswahl value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">Jeder Status</option>
          <option value="offen">{art === 'einnahme' ? 'erwartet' : 'offen'}</option>
          {(art === 'rechnung' || art === 'alle') && <option value="bezahlt">bezahlt</option>}
          {(art === 'angebot' || art === 'alle') && <option value="umgewandelt">umgewandelt</option>}
          {(art === 'angebot' || art === 'alle') && <option value="abgelehnt">abgelehnt</option>}
          {(art === 'einnahme' || art === 'alle') && <option value="erhalten">erhalten</option>}
        </Auswahl>
        <Auswahl value={wer} onChange={(e) => setWer(e.target.value)} aria-label="Person">
          <option value="">Alle Personen</option>
          <option value="__ohne">Niemand zugeordnet</option>
          {mitglieder?.map((m) => (
            <option key={m.benutzerId} value={m.benutzerId}>
              {m.anzeigename}
            </option>
          ))}
          {s.freieNamen.length > 0 && (
            <optgroup label="Ohne Konto">
              {s.freieNamen.map((n) => (
                <option key={n} value={`frei:${n.toLocaleLowerCase('de')}`}>
                  {n}
                </option>
              ))}
            </optgroup>
          )}
        </Auswahl>
      </div>

      {nachholbar > 0 && (
        <Meldung art="info">
          {nachholbar === 1 ? 'Ein Beleg hat' : `${nachholbar} Belege haben`} nur einen Ersatzkurs oder gar keinen Kurs, weil die Kursquelle nicht erreichbar war.{' '}
          {darfSchreiben && (
            <button type="button" className="bu-link" onClick={nachholen} disabled={a.laeuft}>
              {a.laeuft ? 'Holt Kurse …' : 'Kurse jetzt nachholen'}
            </button>
          )}
        </Meldung>
      )}
      <Meldung>{a.fehler}</Meldung>

      {liste.length === 0 ? (
        <Leer titel={s.belege.length ? 'Keine Belege für diese Auswahl' : 'Noch keine Belege'}>
          {!s.belege.length && darfSchreiben && 'Lege eine Rechnung, ein Angebot oder eine Einnahme an – Anlagen wie PDFs lassen sich danach anhängen.'}
        </Leer>
      ) : (
        <div className="bu-liste" role="table" aria-label="Belege">
          <div className="bu-liste-kopf" role="row">
            <span role="columnheader">Datum</span>
            <span role="columnheader">Titel / Von</span>
            <span role="columnheader">{art === 'einnahme' ? 'Labels' : art === 'alle' ? 'Kategorie / Labels' : 'Kategorie'}</span>
            <span role="columnheader">Von wem</span>
            <span role="columnheader">Status</span>
            <span role="columnheader" className="bu-rechts" title="In Euro, brutto. Bei Belegen in einer anderen Währung zeigt Überfahren (Handy: Antippen) den Originalbetrag.">
              Betrag
            </span>
          </div>
          {liste.map((b) => (
            <Zeile key={b.id} b={b} oeffnen={() => oeffnen({ art: 'ansicht', id: b.id })} zeigeArt={art === 'alle'} />
          ))}
          <div className="bu-liste-summe" role="row">
            <span>
              {liste.length} {liste.length === 1 ? 'Beleg' : 'Belege'}
              {(art === 'angebot' || art === 'alle') && <span className="leise klein"> · abgelehnte und umgewandelte Angebote zählen nicht</span>}
            </span>
            <span className="bu-rechts">
              <strong>{summen.join(' · ')}</strong>
              {ohneKurs > 0 && <span className="leise klein"> ({ohneKurs} ohne Kurs nicht mitgezählt)</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Zeile({ b, oeffnen, zeigeArt }: { b: BelegAnsicht; oeffnen(): void; zeigeArt: boolean }) {
  const blass = b.art === 'angebot' && b.status !== 'offen';
  const info = b.art === 'einnahme' ? (b.labels.length ? <LabelMarken labels={b.labels} /> : 'ohne Label') : (b.kategoriePfad ?? '–');
  return (
    <button type="button" className={`bu-liste-zeile${blass ? ' gedimmt' : ''}`} role="row" onClick={oeffnen}>
      <span className="bu-z-datum leise">{b.datum ? datum(b.datum) : '–'}</span>
      <span className="bu-z-titel">
        <strong>
          {zeigeArt && <span className="bu-art-kurz">{ART_NAME[b.art]}</span>}
          {b.titel}
        </strong>
        {b.absender && <span className="leise klein">{b.absender}</span>}
        {b.anzahlAnlagen > 0 && (
          <span className="leise klein bu-anlagen-zahl" title={`${b.anzahlAnlagen} Anlagen`}>
            {Symbol.datei} {b.anzahlAnlagen}
          </span>
        )}
      </span>
      <span className="bu-z-kategorie leise">{info}</span>
      <span className="bu-z-teilnehmer" title={b.teilnehmerFrei ? 'ohne Beholder-Konto' : undefined}>
        {b.teilnehmerName ?? <span className="leise">–</span>}
      </span>
      <span className="bu-z-status">
        <StatusMarke b={b} />
      </span>
      <span className={`bu-z-betrag bu-rechts${b.art === 'einnahme' ? ' bu-text-ok' : ''}`}>
        {b.art === 'einnahme' && '+ '}
        <Euro b={b} />
      </span>
    </button>
  );
}
